import addTrustedSender from '../services/addTrustedSender';
import getItemOnUnblockContent, {
    getItemAttachmentOnUnblockContent,
} from '../services/getItemOnUnblockContent';
import updateItemBlockStatus from '../services/updateItemBlockStatus';
import { ObservableMap } from 'mobx';
import tryRemoveFromMailStoreItems, {
    RemoveItemSource,
} from 'owa-mail-actions/lib/triage/tryRemoveFromMailStoreItems';
import { deleteConversationItemParts } from 'owa-mail-store-actions';
import ConversationItemParts from 'owa-mail-store/lib/store/schema/ConversationItemParts';
import ConversationReadingPaneNode from 'owa-mail-store/lib/store/schema/ConversationReadingPaneNode';
import mailStore from 'owa-mail-store/lib/store/Store';
import Item from 'owa-service/lib/contract/Item';
import Message from 'owa-service/lib/contract/Message';
import SingleRecipientType from 'owa-service/lib/contract/SingleRecipientType';
import { action, select } from 'satcheljs/lib/legacy';

export interface UnblockItemContentState {
    items: ObservableMap<string, Item>;
    conversations: ObservableMap<string, ConversationItemParts>;
    conversationNodes: ObservableMap<string, ConversationReadingPaneNode>;
}

const deleteItemFromTrustedSender = action('deleteItemFromTrustedSender')(
    function deleteItemFromTrustedSender(item: Item, state?: UnblockItemContentState) {
        tryRemoveFromMailStoreItems(item.ItemId.Id, RemoveItemSource.ItemCleanUp);
    }
);

function hasSameEmailAddress(recipient: SingleRecipientType, emailAddress: string): boolean {
    return (
        recipient?.Mailbox?.EmailAddress &&
        recipient.Mailbox.EmailAddress.toLowerCase() == emailAddress.toLowerCase()
    );
}

function shouldKeepItem(item: Item, trustedSenderEmail: string): boolean {
    const message = item as Message;
    return (
        !message.HasBlockedImages ||
        !(
            hasSameEmailAddress(message.From, trustedSenderEmail) ||
            hasSameEmailAddress(message.Sender, trustedSenderEmail)
        )
    );
}

function removeCachedItemsFromTrustedSender(message: Message, state?: UnblockItemContentState) {
    if (!message.Sender || !message.Sender.Mailbox) {
        return;
    }
    const trustedSenderEmail = message.Sender.Mailbox.EmailAddress;

    // Remove all conversations in which an item has sender/from as the trusted sender.
    const conversationKeys = [...state.conversations.keys()];
    for (let i = conversationKeys.length - 1; i >= 0; i--) {
        const conversationId = conversationKeys[i];
        if (conversationId == message.ConversationId.Id) {
            // Do not remove the conversation in which the current item is.
            continue;
        }

        const conversationItemParts = state.conversations.get(conversationId);
        const shouldKeepConversation = conversationItemParts.conversationNodeIds.every(nodeId => {
            const conversationNode = state.conversationNodes.get(nodeId);
            return (
                conversationNode &&
                conversationNode.itemIds.every(itemId => {
                    const item = state.items.get(itemId);
                    return item && shouldKeepItem(item, trustedSenderEmail);
                })
            );
        });

        if (!shouldKeepConversation) {
            deleteConversationItemParts(conversationId);
        }
    }

    // Remove all items that has sender/from as the trusted sender.
    const itemKeys = [...state.items.keys()];
    for (let i = itemKeys.length - 1; i >= 0; i--) {
        const itemId = itemKeys[i];
        if (itemId == message.ItemId.Id) {
            // Do not remove the current item.
            continue;
        }

        const item = state.items.get(itemId);
        if (!shouldKeepItem(item, trustedSenderEmail)) {
            deleteItemFromTrustedSender(item, state);
        }
    }
}

export default select<UnblockItemContentState>({
    items: () => mailStore.items,
    conversations: () => mailStore.conversations,
    conversationNodes: () => mailStore.conversationNodes,
})(
    action('unblockItemContent')(async function unblockItemContent(
        message: Message,
        isTemporary: boolean,
        state?: UnblockItemContentState
    ) {
        // Update block status optimistically so view gets rerendered promptly.
        message.HasBlockedImages = false;
        message.BlockStatus = false;

        try {
            await getItemOnUnblockContent(message);
            if (isTemporary) {
                updateItemBlockStatus(message.ItemId);
            } else {
                removeCachedItemsFromTrustedSender(message, state);
                return addTrustedSender(message.ItemId.Id);
            }
        } catch (err) {
            // Can happen due to network error
            // Just eat the error for the moment
        }
        return Promise.resolve(true);
    })
);

export async function unblockItemContentForItemAttachment(message: Message) {
    try {
        await getItemAttachmentOnUnblockContent(message);
    } catch (err) {
        // Can happen due to network error
        // Just eat the error for the moment
    }
}
